import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaTrash } from "react-icons/fa";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { deleteFile, getFilesByLead } from "../../store/slices/FileDataSlice";
import { isAdminAuthLevel } from "../../utils/opsLeadAttachments";

const FileUploadEditLead = ({
  SelectedCategory,
  setSelectedCategory,
  setFileNames,
  editingLeadReport,
}) => {
  const auth =
    typeof localStorage !== "undefined" ? localStorage.getItem("auth") : "";
  const isAdmin = isAdminAuthLevel(auth);
  const normalizedAuth = String(auth || "").trim().toUpperCase();
  const isDirector = normalizedAuth === "L1";
  const canShowAttachmentControls = !isAdmin;
  const allowedCategoriesForUser = isDirector
    ? ["Quote", "Purchase Order", "Contract"]
    : ["Quote", "Purchase Order", "Contract", "Communication", "Miscellaneous"];

  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const dropdownRefProbability = useRef(null);
  const [editfetchedfiles, seteditFetchedFiles] = useState([]);

  const loadFiles = useCallback(async () => {
    if (isAdmin) return;
    try {
      const files = await dispatch(getFilesByLead(editingLeadReport._id));
      seteditFetchedFiles(files);
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  }, [dispatch, editingLeadReport, isAdmin]);

  useEffect(() => {
    if (!editingLeadReport || isAdmin) return;
    loadFiles();
  }, [loadFiles, editingLeadReport, isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefProbability.current &&
        !dropdownRefProbability.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFileDownload = (fileData) => {
    try {
      if (!fileData || !fileData.filepathurl) {
        throw new Error("Invalid file data");
      }
      const fileUrl = fileData.filepathurl; // Use filepathurl for the download URL
      const fileName = fileData.filename || fileUrl.split("/").pop();
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      link.setAttribute("target", "_blank"); // fallback for some browsers
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      enqueueSnackbar("File download started", { variant: "success" });
    } catch (error) {
      console.error("Download failed:", error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
  };


  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const allowedFormats = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (!SelectedCategory) {
      enqueueSnackbar("Please select a category first", {
        variant: "error",
      });
      return;
    }
    setFileNames((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const validFiles = [];
      selectedFiles.forEach((file) => {
        if (!allowedFormats.includes(file.type)) {
          enqueueSnackbar(`Only PDF, DOC, DOCX, XLS, and XLSX files are allowed (${file.name})`, {
            variant: "error",
          });
          return;
        }
        const isDuplicate = [
          ...existing,
          ...(editfetchedfiles?.payload || [])
        ].some(
          (f) => f.name === file.name ||
            (f.filepath && f.filepath.split('/').pop() === file.name)
        );
        if (isDuplicate) {
          enqueueSnackbar(`File "${file.name}" is already uploaded`, {
            variant: "error",
          });
          return;
        }
        const fileWithCategory = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
        fileWithCategory.selectedCategory = SelectedCategory;
        validFiles.push(fileWithCategory);
      });

      if (validFiles.length === 0) return prev;
      seteditFetchedFiles((prevFetched) => {
        const currentFiles = prevFetched?.payload || [];
        const newFiles = validFiles.map((file) => ({
          id: `temp123`,
          filepath: `uploads/filesdata/${file.name}`,
          filepathurl: URL.createObjectURL(file),
          leadId: editingLeadReport._id,
          selectedCategory: file.selectedCategory,
          isNew: true, // Flag to identify newly uploaded files
          name: file.name, // Keep original name for reference
          type: file.type // Keep file type
        }));
        return {
          ...prevFetched,
          payload: [...currentFiles, ...newFiles]
        };
      });
      return [...existing, ...validFiles];
    });
    setSelectedCategory("");
    e.target.value = null;
  };

  const handleFileDelete = async (file, index) => {
    if (file.id === "temp123") {
      seteditFetchedFiles((prev) => {
        if (prev?.payload) {
          const updatedPayload = prev.payload.filter((_, i) => i !== index);
          return { ...prev, payload: updatedPayload };
        }
        return prev.filter((_, i) => i !== index);
      });
      enqueueSnackbar("File deleted successfully", { variant: "success" });
      return;
    } else {
      const id = file.id;
      try {
        await dispatch(deleteFile(id)).unwrap(); // Correct thunk dispatch
        const updatedFiles = await dispatch(getFilesByLead(editingLeadReport._id));
        seteditFetchedFiles(updatedFiles);
        enqueueSnackbar("File deleted successfully", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Error occurred while deleting file", { variant: "error" });
        console.error("Error deleting file:", error);
      } finally {
        loadFiles()
      }
    }
  };

  const fetchedVisibleToUser = isAdmin
    ? []
    : (editfetchedfiles?.payload || []).filter((f) =>
        allowedCategoriesForUser.includes(String(f?.category || f?.selectedCategory || "").trim())
      );

  return (
    <div>
      <div>
        <label className="block text-white" htmlFor="date">
          Upload file
        </label>
        <button
          onClick={() => setShowModal(true)}
          className="py-2 px-7  bg-black hover:text-white border hover:bg-slate-800 border-black overflow-hidden shadow-md text-white rounded-lg"
        >
          Upload File
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 flex justify-center items-center z-50 bg-opacity-50 bg-black">
          <div className="w-[50%] bg-white shadow-lg rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold">Attachments</h2>
              <button
                className="text-gray-500 hover:text-black"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
            {canShowAttachmentControls && (
              <div className="flex gap-2">
                <div className="relative bg-white">
                  <div
                    ref={dropdownRefProbability}
                    className=" flex  p-0  rounded-xl align-middle text-[12px]"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setShowCategoryDropdown(!showCategoryDropdown)
                      }
                      className="border relative w-24 h-8 border-gray-400 text-nowrap rounded-md p-2 shadow-md overflow-hidden bg-gray-200 outline-none flex items-center justify-between"
                    >
                      <p className="text-left text-xs truncate">
                        {SelectedCategory ? SelectedCategory : "Category"}
                      </p>
                      <ExpandMoreIcon
                        color="white"
                        className="cursor-pointer bg-gray-200 flex-shrink-0"
                      />
                    </button>

                    {showCategoryDropdown && (
                      <div
                        style={{ zIndex: 9999 }}
                        className="absolute top-full left-0 w-40 p-1 cursor-pointer max-h-96 overflow-y-auto bg-white text-sm border border-gray-300 shadow-lg flex flex-col gap-1"
                      >
                      {allowedCategoriesForUser.map((cat) => (
                        <div
                          key={cat}
                          className="p-1 hover:bg-gray-200 cursor-pointer"
                          onClick={() => {
                            setSelectedCategory(cat);
                            setShowCategoryDropdown(!showCategoryDropdown);
                          }}
                        >
                          {cat}
                        </div>
                      ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="file"
                    multiple
                    id={`fileUpload`}
                    className="hidden"
                    onChange={(e) => handleFileChange(e)}
                    disabled={!SelectedCategory}
                  />

                  <label
                    htmlFor="fileUpload"
                    className={`text-xs p-2 w-24 h-8 text-center rounded-md flex items-center justify-center ${!SelectedCategory
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-gray-800 text-white hover:text-black hover:bg-gray-300 cursor-pointer"
                      }`}
                  >
                    Upload File
                  </label>

                  <p className="truncate text-xs pl-1">
                    {editfetchedfiles?.name || "No file selected"}
                  </p>
                  <div className="flex gap-2 text-red-500 text-[12px] pl-3">
                    <p> only .pdf, .xlsx and .docx allowed</p>
                  </div>
                </div>
              </div>
            )}
            <div className="text-[13px]">
              {isAdmin ? (
                <div className="py-4 px-4 text-center text-gray-500">
                  No attachments available.
                </div>
              ) : (
                <>
                  <div>
                    <div className="border-b flex w-full">
                      <div className="py-2 px-4 w-[15%]">Sr. No.</div>
                      <div className="py-2 px-4 w-[40%]">File Name</div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Category
                      </div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Download
                      </div>
                      <div className="py-2 px-4 text-center w-[15%]">
                        Delete
                      </div>
                    </div>
                  </div>
                  <div>
                    {fetchedVisibleToUser.map((file, index) => (
                      <div
                        className="border-b flex w-full"
                        key={file.id ?? index}
                      >
                        <div className="py-2 px-4 w-[15%]">{index + 1}</div>
                        <div className="py-2 px-4 w-[40%] truncate overflow-hidden">
                          {file.filename || file.name}
                        </div>
                        <div className="py-2 px-4 w-[15%] text-center truncate">
                          {file.category || file.selectedCategory}
                        </div>
                        <div className="py-2 px-4 text-center w-[15%]">
                          <button
                            onClick={() => handleFileDownload(file)}
                            className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-700"
                          >
                            <CloudDownloadIcon />
                          </button>
                        </div>
                        <div className="py-2 px-4 text-center w-[15%]">
                          <button
                            onClick={() => handleFileDelete(file, index)}
                            className="bg-red-500 text-white px-2 py-2 rounded hover:bg-red-700"
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadEditLead;
